import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ENTITY_TYPE_LABELS, type EntityType } from '@raro/domain';
import { Button, Drawer, Label, Serif, cx } from '@raro/ui';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { api, API_URL } from '../lib/api';
import type { EntitySummary, WallItem } from '../lib/types';
import { EntityPicker } from './EntityPicker';

interface View {
  x: number;
  y: number;
  scale: number;
}

/** PAREDE — canvas pan/zoom com itens arrastáveis. Posições persistem na API. */
export function WallCanvas({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const wall = useQuery({ queryKey: ['wall', projectId], queryFn: () => api.get<{ items: WallItem[] }>(`/projects/${projectId}/wall`) });
  const [view, setView] = useState<View>({ x: 24, y: 24, scale: 0.9 });
  const [local, setLocal] = useState<Record<string, { x: number; y: number }>>({});
  const [picking, setPicking] = useState(false);
  const panning = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const dragging = useRef<{ id: string; sx: number; sy: number; ox: number; oy: number; cx: number; cy: number } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const move = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) => api.patch(`/wall/${id}`, { x, y }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wall', projectId] }),
  });
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/projects/${projectId}/wall`, body),
    onSuccess: () => {
      setPicking(false);
      void qc.invalidateQueries({ queryKey: ['wall', projectId] });
      void qc.invalidateQueries({ queryKey: ['summary', projectId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/wall/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wall', projectId] });
      void qc.invalidateQueries({ queryKey: ['summary', projectId] });
    },
  });
  const editNote = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => api.patch(`/wall/${id}`, { text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wall', projectId] }),
  });

  // zoom com scroll
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setView((v) => {
        const scale = Math.min(2, Math.max(0.3, v.scale * (e.deltaY > 0 ? 0.92 : 1.08)));
        const k = scale / v.scale;
        return { scale, x: mx - (mx - v.x) * k, y: my - (my - v.y) * k };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onCanvasDown = (e: ReactPointerEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('.rr-wall-item')) return;
    panning.current = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    setIsPanning(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onItemDown = (e: ReactPointerEvent, item: WallItem) => {
    if (!canEdit || e.button !== 0 || (e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).closest('button')) return;
    e.stopPropagation();
    const pos = local[item.id] ?? { x: item.x, y: item.y };
    dragging.current = { id: item.id, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, cx: pos.x, cy: pos.y };
    setDragId(item.id);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = useCallback(
    (e: ReactPointerEvent) => {
      if (dragging.current) {
        const d = dragging.current;
        d.cx = Math.round(d.ox + (e.clientX - d.sx) / view.scale);
        d.cy = Math.round(d.oy + (e.clientY - d.sy) / view.scale);
        setLocal((l) => ({ ...l, [d.id]: { x: d.cx, y: d.cy } }));
      } else if (panning.current) {
        const p = panning.current;
        setView((v) => ({ ...v, x: p.ox + (e.clientX - p.sx), y: p.oy + (e.clientY - p.sy) }));
      }
    },
    [view.scale],
  );
  const onUp = () => {
    if (dragging.current) {
      const d = dragging.current;
      if (d.cx !== d.ox || d.cy !== d.oy) {
        setLocal((l) => ({ ...l, [d.id]: { x: d.cx, y: d.cy } }));
        move.mutate({ id: d.id, x: d.cx, y: d.cy });
      }
      dragging.current = null;
      setDragId(null);
    }
    panning.current = null;
    setIsPanning(false);
  };

  const items = wall.data?.items ?? [];

  return (
    <>
      <div ref={ref} className={cx('rr-wall', isPanning && 'rr-wall--panning')} onPointerDown={onCanvasDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        <div className="rr-wall__toolbar">
          {canEdit && (
            <>
              <Button size="sm" variant="primary" onClick={() => setPicking(true)}>+ colocar na parede</Button>
              <Button size="sm" variant="quiet" onClick={() => add.mutate({ kind: 'note', text: 'o que estamos assumindo?', x: Math.round((80 - view.x) / view.scale), y: Math.round((80 - view.y) / view.scale), color: 'paper' })}>
                + nota
              </Button>
            </>
          )}
          <Button size="sm" variant="quiet" onClick={() => setView((v) => ({ ...v, scale: Math.max(0.3, v.scale - 0.1) }))}>−</Button>
          <span className="rr-wall__zoom">{Math.round(view.scale * 100)}%</span>
          <Button size="sm" variant="quiet" onClick={() => setView((v) => ({ ...v, scale: Math.min(2, v.scale + 0.1) }))}>+</Button>
          <Button size="sm" variant="quiet" onClick={() => setView({ x: 24, y: 24, scale: 0.9 })}>⌂</Button>
        </div>

        {wall.isSuccess && items.length === 0 && (
          <div className="rr-wall__empty">
            <div className="rr-stack rr-stack--2" style={{ textAlign: 'center', maxWidth: 380 }}>
              <p className="rr-heading"><Serif italic>a parede está vazia.</Serif></p>
              <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>fotos, frases, evidências, sinais, hipóteses, contradições. tudo que importa, lado a lado.</p>
            </div>
          </div>
        )}

        <div className="rr-wall__canvas" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
          {items.map((item) => {
            const pos = local[item.id] ?? { x: item.x, y: item.y };
            return (
              <div
                key={item.id}
                className={cx('rr-wall-item', `rr-wall-item--${item.kind === 'ref' ? item.refType : item.kind}`, item.color === 'yellow' && 'rr-wall-item--yellow', dragId === item.id && 'rr-wall-item--dragging')}
                style={{ left: pos.x, top: pos.y, width: item.w, transform: `rotate(${item.rotation}deg)`, zIndex: dragId === item.id ? 999 : item.z }}
                onPointerDown={(e) => onItemDown(e, item)}
              >
                <WallItemBody item={item} canEdit={canEdit} onEdit={(text) => editNote.mutate({ id: item.id, text })} onRemove={() => remove.mutate(item.id)} />
              </div>
            );
          })}
        </div>
      </div>

      <Drawer open={picking} onClose={() => setPicking(false)} eyebrow="Parede" title="o que vai para a parede?">
        <div className="rr-stack rr-stack--4">
          <Label>fontes, evidências, observações, perguntas, sinais e hipóteses deste projeto</Label>
          <EntityPicker
            projectId={projectId}
            exclude={items.filter((i) => i.refType && i.refId).map((i) => ({ type: i.refType as EntityType, id: i.refId! }))}
            onSelect={(e: EntitySummary) => add.mutate({ kind: 'ref', refType: e.type, refId: e.id, x: Math.round((120 - view.x) / view.scale), y: Math.round((120 - view.y) / view.scale) })}
          />
        </div>
      </Drawer>
    </>
  );
}

function WallItemBody({ item, canEdit, onEdit, onRemove }: { item: WallItem; canEdit: boolean; onEdit: (t: string) => void; onRemove: () => void }) {
  const [text, setText] = useState(item.text ?? '');
  const [editing, setEditing] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const removeBtn = canEdit ? (
    <button type="button" className="rr-wall-item__remove" aria-label="Tirar da parede" onClick={onRemove}>×</button>
  ) : null;

  if (item.kind === 'note') {
    return (
      <>
        <div className="rr-wall-item__kind"><span>nota{canEdit && !editing ? ' · clique duplo para editar' : ''}</span>{removeBtn}</div>
        {canEdit ? (
          <textarea
            ref={taRef}
            rows={3}
            value={text}
            readOnly={!editing}
            style={{ pointerEvents: editing ? 'auto' : 'none' }}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
              setEditing(false);
              if (text !== item.text) onEdit(text);
            }}
          />
        ) : (
          <div>{item.text}</div>
        )}
        {canEdit && !editing && (
          <div
            style={{ position: 'absolute', inset: 0 }}
            onDoubleClick={() => {
              setEditing(true);
              setTimeout(() => taRef.current?.focus(), 0);
            }}
          />
        )}
      </>
    );
  }
  if (item.kind === 'image' && item.file) {
    return (
      <>
        <img src={`${API_URL}${item.file.url}`} alt="" />
        {removeBtn}
      </>
    );
  }
  const e = item.entity;
  if (!e) return <div className="rr-wall-item__text rr-faint">(removido)</div>;
  const label = e.type === 'signal' ? `sinal · ${String(e.meta.kind ?? '')}` : e.type === 'evidence' ? `evidência · ${String(e.meta.evidenceType ?? '')}` : ENTITY_TYPE_LABELS[e.type];
  return (
    <>
      <div className="rr-wall-item__kind"><span>{label}</span>{removeBtn}</div>
      {e.type === 'hypothesis' && <div className="rr-wall-item__title">{e.title}</div>}
      {e.type === 'source' && <div className="rr-wall-item__title">{e.title}</div>}
      <div className="rr-wall-item__text">{e.type === 'evidence' ? `“${e.text}”` : e.type === 'hypothesis' || e.type === 'source' ? e.text : e.title}</div>
    </>
  );
}
